import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type G2Info, IMPORT_STATE } from '../types';

export const useG2 = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspace_alias = currWorkspace?.alias;
  if (!workspace_alias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(SOURCE_TYPE.G2, workspace_alias);

  const { data, refetch } = useQuery({
    queryKey: queryKeys,
    queryFn: () => service.g2.getG2Info(workspace_alias),
    enabled: enabled,
  });

  const { mutate: disconnectG2Mutation } = useMutation({
    mutationFn: () => service.g2.deleteG2Info(workspace_alias),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.g2.importG2Feedback(workspace_alias, dateRange),
    onSuccess: () => {
      const g2Workspace = queryClient.getQueryData(queryKeys) as G2Info;
      queryClient.setQueryData(queryKeys, {
        ...g2Workspace,
        import_state: IMPORT_STATE.IMPORTING,
      });
    },
  });

  return {
    g2Workspace: data,
    disconnectG2Mutation,
    requestImportMutation,
    refetch,
  };
};
